import {Color} from '@benchling/nucleus-ui';
import {StyleSheet, css} from 'aphrodite';
import React from 'react';

import {EntityAssociations} from 'modbio/complex-polymers/ComplexPolymerConstants';
import {getPolymerRefToEntityIdMap} from 'modbio/complex-polymers/ComplexPolymerUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import BioentityChip from 'ui-library/chips/BioentityChip';
import {ChipSize} from 'ui-library/chips/Chip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {ConjugateSelectionParams} from './CreateComplexPolymerAttachConjugatePane';
import {
  CreateComplexPolymerMoleculeGraphQL,
  CreateComplexPolymerOligoGraphQL,
} from './CreateComplexPolymerUtil';

interface ConjugateOptionProps {
  id: string;
  idx: number;
  polymerRef: string;
  schemaName: string;
  isCurrent: boolean;
  isAttached: boolean;
  setCurrentRefIdx: (idx: number) => void;
}

export function ConjugateOption({
  id,
  idx,
  polymerRef,
  schemaName,
  isCurrent,
  isAttached,
  setCurrentRefIdx,
}: ConjugateOptionProps): JSX.Element {
  return (
    <div
      className={isCurrent ? css(styles.selectedConjugateOption) : css(styles.conjugateOption)}
      onClick={() => {
        setCurrentRefIdx(idx);
      }}
      {...getTestProps({
        component: 'ConfigureComplexPolymerAttachmentsStep',
        element: 'conjugateOption',
        key: idx.toString(),
      })}
      data-amp-safe={false}
    >
      <span>
        <BenchlingIcon
          className={
            isAttached ? css(styles.attachedConjugateSelectionIcon) : css(styles.conjugateSelectionIcon)
          }
          icon={isAttached ? 'check-circle' : 'circle-outline'}
        />
        <span>
          <BioentityChip id={id} size={ChipSize.MINI} />
          <div className={css(styles.conjugateSchemaText)}>{schemaName}</div>
        </span>
      </span>
    </div>
  );
}

interface ConjugateOptionData {
  id: string;
  polymerRef: string;
  schemaName: string;
}

interface EntityIdToConjugateMap {
  [id: string]: CreateComplexPolymerMoleculeGraphQL | CreateComplexPolymerOligoGraphQL;
}

function getEntityIdToConjugateMap(
  conjugates: Array<CreateComplexPolymerMoleculeGraphQL | CreateComplexPolymerOligoGraphQL>
): EntityIdToConjugateMap {
  const entityIdToConjugate: EntityIdToConjugateMap = {};
  conjugates.forEach((conjugate) => {
    entityIdToConjugate[conjugate.id] = conjugate;
  });

  return entityIdToConjugate;
}

export interface ConjugateSelectionPanelProps {
  conjugates: Array<CreateComplexPolymerMoleculeGraphQL | CreateComplexPolymerOligoGraphQL>;
  attachedConjugateRefs: Array<string>;
  conjugateSelectionParams: ConjugateSelectionParams;
  entityAssociations: EntityAssociations;
}

export function ConjugateSelectionPanel({
  conjugates,
  attachedConjugateRefs,
  conjugateSelectionParams,
  entityAssociations,
}: ConjugateSelectionPanelProps): JSX.Element {
  const conjugateOptionData: Array<ConjugateOptionData> = [];
  const polymerRefToEntityId = getPolymerRefToEntityIdMap(entityAssociations);
  const conjugatesByEntityId = getEntityIdToConjugateMap(conjugates);

  const {selectedConjugateRefs, currentConjugateRefIdx, setCurrentConjugateRefIdx} = conjugateSelectionParams;
  const currentConjugateRef = selectedConjugateRefs[currentConjugateRefIdx];

  selectedConjugateRefs.forEach((polymerRef) => {
    const entityId = polymerRefToEntityId[polymerRef];
    const conjugateEntity = conjugatesByEntityId[entityId];
    const optionData = {
      id: conjugateEntity.id,
      polymerRef,
      schemaName: conjugateEntity.tagSchema!.name,
    };
    conjugateOptionData.push(optionData);
  });

  return (
    <div className={css(styles.conjugateSelectionPanel)}>
      <div className={css(styles.conjugateSelectionPanelHeader)}>COMPONENTS ({conjugates.length})</div>
      <div className={css(styles.conjugateOptions)}>
        {conjugateOptionData.map((optionData, idx) => (
          <ConjugateOption
            key={optionData.polymerRef}
            id={optionData.id}
            idx={idx}
            polymerRef={optionData.polymerRef}
            schemaName={optionData.schemaName}
            isCurrent={currentConjugateRef === optionData.polymerRef}
            isAttached={attachedConjugateRefs.includes(optionData.polymerRef)}
            setCurrentRefIdx={setCurrentConjugateRefIdx}
          />
        ))}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  conjugateOption: {
    borderBottom: `1px solid ${Color.GRAY_300}`,
    padding: '8px',
    display: 'flex',
    justifyContent: 'space-between',
  },
  conjugateOptions: {
    overflow: 'auto',
    height: '375px',
  },
  selectedConjugateOption: {
    borderBottom: `1px solid ${Color.GRAY_300}`,
    padding: '8px 8px 8px 5px',
    borderLeft: `3px solid ${Color.BLUE_400}`,
    backgroundColor: '#F4F5FF',
    display: 'flex',
    justifyContent: 'space-between',
  },
  conjugateSelectionIcon: {
    color: Color.GRAY_500,
    fontSize: '14px',
    marginRight: '8px',
  },
  attachedConjugateSelectionIcon: {
    color: '#00A997',
    fontSize: '14px',
    marginRight: '8px',
  },
  conjugateSchemaText: {
    color: Color.GRAY_700,
    marginLeft: '22px',
    marginTop: '5px',
  },
  removeConjugateButton: {
    color: Color.GRAY_400,
  },
  conjugateSelectionPanel: {
    height: '400px',
  },
  conjugateSelectionPanelHeader: {
    fontWeight: 600,
    fontSize: '11px',
    color: Color.GRAY_700,
    letterSpacing: '1px',
    padding: '10px',
  },
});
